import React, { useEffect } from "react";
import {
  useSearchParams,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { FloatingToolbar } from "./components/Widget/FloatingToolbar";
import { DashboardLayout } from "./components/Dashboard/DashboardLayout";
import { LandingPage } from "./components/Landing/LandingPage";
import { SignUpPage } from "./components/Auth/SignUpPage";
import { SignInPage } from "./components/Auth/SignInPage";
import { useAuthStore } from "./store/authStore";
import { useSubscriptionStore } from "./store/subscriptionStore";

interface AppProps {
  mode?: "widget" | "dashboard" | "landing";
  projectId?: string;
}

export default function App({ mode = "landing", projectId }: AppProps) {
  const { user, initialized } = useAuthStore();
  const checkSubscriptionStatus = useSubscriptionStore(
    (state) => state.checkSubscriptionStatus
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentMode = searchParams.get("mode") || mode;
  const section = searchParams.get("section");

  // Ensure widget mode persists when navigation happens
  useEffect(() => {
    if (mode === "widget" && currentMode !== "widget") {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("mode", "widget");
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true }
      );
    }
  }, [mode, currentMode, searchParams, location.pathname, navigate]);

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus(user.id);
    }
  }, [user]);

  if (!initialized) {
    return <div>Loading...</div>;
  }

  if (currentMode === "widget") {
    return (
      <div className="relative min-h-screen">
        <FloatingToolbar projectId={projectId!} />
      </div>
    );
  }

  if (currentMode?.startsWith("dashboard") && user) {
    return <DashboardLayout section={section || "projects"} />;
  }

  return (
    <div className="fixed inset-0 z-[100] bg-midnight-950 overflow-y-auto">
      {currentMode === "landing" && <LandingPage />}
      {currentMode === "signup" && <SignUpPage />}
      {currentMode === "signin" && <SignInPage />}
    </div>
  );
}
